/* global globalThis */
import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import PropTypes from "prop-types";
import { useLocation, Link } from "react-router-dom";
import {
  Box,
  CssBaseline,
  Button,
  useTheme,
  useMediaQuery,
} from "@mui/material";

import FilterListRoundedIcon from "@mui/icons-material/FilterListRounded";
import MobileCommonFilters from "../common/mobileCommonFilters/CommonFilters";
import { styled } from "@mui/material/styles";

import { fetchProviderOutreach } from "../../api/provider";
import _ from "lodash";

import CommonSearchBar from "../common/searchBar/CommonSearchBar";
import OldCommonFilters from "../common/commonFilters/OldCommonFilters";
import CommonTable from "../common/table/commonTable";

import PageHeader from "../common/PageHeader";
import StatusIndicators from "../common/StatusIndicators";
import MemberPharmacyQuickview from "./memberPharmacyQuickview";
import { getPharmacyColumns } from "../common/table/columns";
import {
  fetchPharmacyMember,
  fetchPharmacyProvider,
  fetchPharmacyPrograms,
  fetchPharmacyPharmacies,
} from "../../api/filterType";

import "./Searchpage.css";
import { useLoading } from "../../context/LoadingContext";

const MainContent = styled("div", {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  flexGrow: 1,
  padding: 0,
  margin: 0,
  width: open ? "calc(100% - 240px)" : "100%",
  marginLeft: open ? "240px" : "0",
  minHeight: "80vh",
  backgroundColor: "#F7F8FB",
  overflow: "hidden !important",
  transition: theme.transitions.create(["margin", "width"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
}));

function getScreenInches() {
  const { width, height } = globalThis.screen;
  const dpi = globalThis.devicePixelRatio * 96;
  const widthInInches = width / dpi;
  const heightInInches = height / dpi;
  return Math.hypot(widthInInches, heightInInches);
}

const SECTIONS = {
  members: {
    description: "Search and manage member information",
  },
  providers: {
    title: "Providers",
    description: "Search and manage provider information",
  },
  pharmacies: {
    title: "Pharmacies",
    description: "Search and manage pharmacy information",
  },
  programs: {
    title: "Programs",
    description: "Search and manage program information",
  },
};

const normalizeResponseData = (response) => {
  let content = [];
  let totalElements = 0;

  if (Array.isArray(response)) {
    content = response;
    totalElements = response.length;
  } else {
    if (Array.isArray(response?.content)) {
      content = response.content;
    } else if (Array.isArray(response)) {
      content = response;
    }

    if (typeof response?.totalElements === "number") {
      totalElements = response.totalElements;
    } else if (Array.isArray(response?.content)) {
      totalElements = response.content.length;
    } else if (Array.isArray(response)) {
      totalElements = response.length;
    }
  }

  return { content, totalElements };
};

const processDropdownData = (data, nameKey, idKey) => {
  const map = {};
  const names = [];

  for (const item of data) {
    if (item[nameKey] && item[idKey]) {
      map[item[nameKey]] = item[idKey];
      names.push(item[nameKey]);
    }
  }
  return { map, names };
};

const ProviderOutreach = (props) => {
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize] = useState(30);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const tableRef = useRef(null);

  const [results, setResults] = React.useState({
    content: [],
    totalElements: 0,
  });
  const [totalRows, setTotalRows] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filtersVisible, setFiltersVisible] = useState(false);

  // Lazy loading refs
  const isFetchingMoreRef = useRef(false);
  const fetchSeqRef = useRef(0);
  const lastFetchSignatureRef = useRef(null);
  const filterChangeRef = useRef(false);

  const [mobileOpen] = useState(false);

  const location = useLocation();

  const [memberNameToIdMap, setMemberNameToIdMap] = useState({});
  const [providerNameToNpiMap, setProviderNameToNpiMap] = useState({});
  const [pharmacyNameToIdMap, setPharmacyNameToIdMap] = useState({});
  const [programNameToIdMap, setProgramNameToIdMap] = useState({});

  const isMemberMonitoring = location.pathname.includes("monitoring");
  const { showLoading, hideLoading } = useLoading();

  const isPriorityOutreach =
    props.isPriorityOutreach === undefined
      ? location.pathname.includes("priority-outreach")
      : props.isPriorityOutreach;

  const [memberFilter, setMemberFilter] = useState([]);
  const [providerFilter, setProviderFilter] = useState([]);
  const [pharmacyFilter, setPharmacyFilter] = useState([]);
  const [programFilter, setProgramFilter] = useState([]);

  const countActiveFilters = useCallback(() => {
    return [
      ...(memberFilter || []),
      ...(providerFilter || []),
      ...(pharmacyFilter || []),
      ...(programFilter || []),
    ].filter(Boolean).length;
  }, [memberFilter, providerFilter, pharmacyFilter, programFilter]);

  const activeFilterCount = countActiveFilters();

  const [members, setMembers] = useState([]);
  const [memberIds, setMemberIds] = useState([]);
  const [providers, setProviders] = useState([]);
  const [pharmacies, setPharmacies] = useState([]);
  const [programs, setPrograms] = useState([]);
  const searchRef = useRef(null);
  const isSearchTriggered = useRef(false);
  const [isResetSearch, setIsResetSearch] = useState(false);
  const [searchText, setSearchText] = useState("");

  const [selectedProviderNpis, setSelectedProviderNpis] = useState([]);
  const [selectedPharmacyIds, setSelectedPharmacyIds] = useState([]);
  const [selectedProgramIds, setSelectedProgramIds] = useState([]);

  const [quickViewMember, setQuickViewMember] = useState(null);
  const [selectedMember, setSelectedMember] = useState(null);

  const [filterSearch, setFilterSearch] = useState("");

  const loadMemberNotes = useCallback((memberId) => {
    if (!memberId) return [];

    try {
      const savedNotes = localStorage.getItem(`member_${memberId}_notes`);

      return savedNotes ? JSON.parse(savedNotes) : [];
    } catch (error) {
      console.error("Unexpected error", error);
      return [];
    }
  }, []);

  const handleQuickView = useCallback(
    (row) => {
      if (!row) return;

      const member =
        row.member && typeof row.member === "object" ? row.member : row;

      const memberId = row.key;
      if (memberId) {
        setSelectedMember({
          member: member,
          provider: row.provider,
          pharmacy: row.pharmacy,
          recentActivity: row.recentActivity,
          key: row.key,
          notes: loadMemberNotes(memberId),
        });
        setQuickViewMember(memberId);
      }
    },
    [loadMemberNotes],
  );

  const handleCloseQuickView = useCallback(() => {
    setQuickViewMember(null);
    setSelectedMember(null);
  }, []);

  const columns = getPharmacyColumns(handleQuickView);

  const MobileFilterWrapper = styled("div")(({ theme }) => ({
    [theme.breakpoints.down("md")]: {
      "& .common-filters": {
        display: "flex !important",
        flexDirection: "row !important",
        flexWrap: "nowrap !important",
        overflowX: "auto !important",
        gap: "8px !important",
        paddingBottom: "4px !important",
        alignItems: "center !important",
        "&::-webkit-scrollbar": {
          display: "none",
        },
      },
      "& .common-filter-input": {
        flex: "0 0 auto !important",
        minWidth: "auto !important",
        width: "auto !important",
      },
      "& .filter-label": {
        display: "none !important",
      },
      "& .filter-input-container": {
        height: "36px",
        minWidth: "auto !important",
        width: "fit-content !important",
        maxWidth: "150px",
        backgroundColor: "#fff",
        border: "1px solid #E0E0E0",
        borderRadius: "8px",
      },
      "& .filter-input": {
        fontSize: "13px",
        padding: "6px 32px 6px 8px !important",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
      },
    },
  }));

  const handleSearch = useMemo(
    () =>
      _.debounce((text) => {
        setMemberIds([]);
        setSelectedProviderNpis([]);
        setSelectedPharmacyIds([]);
        setSelectedProgramIds([]);
        setMemberFilter("");
        setProviderFilter("");
        setPharmacyFilter("");
        setProgramFilter("");
        setSearchText(text);
        isSearchTriggered.current = true;
        setPageIndex(0);
        if (text === "") {
          setIsResetSearch(true);
          setTimeout(() => setIsResetSearch(false), 100);
        }
      }, 500),
    [],
  );

  const handleFilterSearch = useMemo(
    () =>
      _.debounce((text) => {
        setFilterSearch(text);
        isSearchTriggered.current = true;
        setPageIndex(0);
        if (text === "") {
          setIsResetSearch(true);
          setTimeout(() => setIsResetSearch(false), 100);
        }
      }, 500),
    [],
  );

  const loadInitialData = useCallback(
    async (page = 0, size = 30, search = "") => {
      try {
        const mySeq = ++fetchSeqRef.current;
        setLoading(true);
        if (page > 0) {
          isFetchingMoreRef.current = true;
        }
        showLoading();

        const requestBody = {
          memberIds: Array.isArray(memberIds) ? memberIds : [],
          providerIds: Array.isArray(selectedProviderNpis)
            ? selectedProviderNpis
            : [],
          pharmacyIds: Array.isArray(selectedPharmacyIds)
            ? selectedPharmacyIds
            : [],
          programIds: Array.isArray(selectedProgramIds)
            ? selectedProgramIds
            : [],
        };

        const signature = JSON.stringify({
          page: Number(page),
          size: Number(size),
          search: search.trim(),
          outreachType: "PHARMACY_OUTREACH",
          body: requestBody,
        });

        if (lastFetchSignatureRef.current === signature) {
          setLoading(false);
          hideLoading();
          isFetchingMoreRef.current = false;
          return;
        }
        lastFetchSignatureRef.current = signature;

        const response = await fetchProviderOutreach({
          page: Number(page),
          size: Number(size),
          search: search.trim(),
          outreachType: "PHARMACY_OUTREACH",
          body: requestBody,
        });

        if (mySeq !== fetchSeqRef.current) {
          return;
        }

        const normalized = normalizeResponseData(response);

        if (page === 0) {
          setResults(normalized);
        } else {
          setResults((prev) => ({
            content: [...(prev?.content || []), ...(normalized.content || [])],
            totalElements:
              typeof normalized.totalElements === "number"
                ? normalized.totalElements
                : prev?.totalElements || 0,
          }));
        }

        setTotalRows((prev) => {
          const returnedTotal =
            typeof normalized.totalElements === "number"
              ? normalized.totalElements
              : 0;
          const returnedCount = normalized.content?.length || 0;
          const currentTotalContent =
            page === 0 ? returnedCount : (prev || 0) + returnedCount;

          if (returnedTotal === returnedCount && returnedCount === size) {
            return currentTotalContent + 1;
          }

          return returnedTotal || currentTotalContent;
        });

        setLoading(false);
      } catch (err) {
        console.error("Error fetching pharmacy outreach data:", err);
        setLoading(false);
        setError("Failed to load data. Please try again.");
        hideLoading();
      } finally {
        isFetchingMoreRef.current = false;
        filterChangeRef.current = false;
        hideLoading();
      }
    },
    [
      memberIds,
      selectedProviderNpis,
      selectedPharmacyIds,
      selectedProgramIds,
      showLoading,
      hideLoading,
    ],
  );

  useEffect(() => {
    loadInitialData(pageIndex, pageSize, searchText);
  }, [
    pageIndex,
    pageSize,
    searchText,
    memberIds,
    selectedProviderNpis,
    selectedPharmacyIds,
    selectedProgramIds,
    loadInitialData,
  ]);

  useEffect(() => {
    filterChangeRef.current = true;
    setResults({ content: [], totalElements: 0 });
    setTotalRows(0);
    isFetchingMoreRef.current = false;
    setPageIndex(0);
    lastFetchSignatureRef.current = null;
  }, [
    memberIds,
    selectedProviderNpis,
    selectedPharmacyIds,
    selectedProgramIds,
  ]);

  useEffect(() => {
    const el = tableRef.current?.scrollElement;
    if (!el) return;

    const onScroll = () => {
      if (filterChangeRef.current || loading || isFetchingMoreRef.current)
        return;
      const canLoadMore = (results?.content?.length || 0) < (totalRows || 0);
      if (!canLoadMore) return;
      const threshold = 200;
      if (el.scrollTop + el.clientHeight >= el.scrollHeight - threshold) {
        isFetchingMoreRef.current = true;
        setPageIndex((p) => p + 1);
      }
    };

    el.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      el.removeEventListener("scroll", onScroll);
    };
  }, [loading, results?.content?.length, totalRows]);

  const updateDropdowns = useCallback(
    async ({ page = 0, size = 30, search = "" } = {}) => {
      try {
        const [memberData, providerData, pharmacyData, programData] =
          await Promise.all([
            fetchPharmacyMember({
              page,
              size,
              search,
              outreachType: "PHARMACY_OUTREACH",
            }),
            fetchPharmacyProvider({
              page,
              size,
              search,
              outreachType: "PHARMACY_OUTREACH",
            }),
            fetchPharmacyPharmacies({
              page,
              size,
              search,
              outreachType: "PHARMACY_OUTREACH",
            }),
            fetchPharmacyPrograms({
              page,
              size,
              search,
              outreachType: "PHARMACY_OUTREACH",
            }),
          ]);

        const { map: memberMap, names: memberNames } = processDropdownData(
          memberData,
          "memberName",
          "memberId",
        );
        setMembers(memberNames);
        setMemberNameToIdMap(memberMap);
        const { map: providerMap, names: providerNames } = processDropdownData(
          providerData,
          "providerName",
          "providerIds",
        );
        setProviders(providerNames);
        setProviderNameToNpiMap(providerMap);

        const { map: pharmacyNameToIdMap, names: pharmacyNames } =
          processDropdownData(pharmacyData, "pharmacyName", "pharmacyId");
        setPharmacies(pharmacyNames);
        setPharmacyNameToIdMap(pharmacyNameToIdMap);

        const { map: programMap, names: programNames } = processDropdownData(
          programData,
          "programName",
          "programId",
        );
        setPrograms(programNames);
        setProgramNameToIdMap(programMap);
      } catch (error) {
        console.error("Unexpected error", error);
        setMembers([]);
        setMemberIds([]);
        setPrograms([]);
        setPharmacies([]);
        setProviders([]);
      }
    },
    [],
  );
  useEffect(() => {
    updateDropdowns({
      page: pageIndex,
      size: pageSize,
      search: filterSearch,
    });
  }, [updateDropdowns, pageIndex, pageSize, filterSearch]);

  const handleMemberChange = (selectedMemberNames) => {
    let selected = [];
    if (Array.isArray(selectedMemberNames)) {
      selected = selectedMemberNames;
    } else if (selectedMemberNames) {
      selected = [selectedMemberNames];
    }

    setMemberFilter(selected);

    const selectedMemberIds = selected.map(
      (name) => memberNameToIdMap[name] || name,
    );
    setMemberIds(selectedMemberIds);
    setPageIndex(0);

    setMemberIds(selectedMemberIds);
  };

  const handleProviderChange = (selectedProviderNames) => {
    let selected = [];
    if (Array.isArray(selectedProviderNames)) {
      selected = selectedProviderNames;
    } else if (selectedProviderNames) {
      selected = [selectedProviderNames];
    }

    setProviderFilter(selected);

    const selectedNpis = selected.map(
      (name) => providerNameToNpiMap[name] || name,
    );
    setSelectedProviderNpis(selectedNpis);
    setPageIndex(0);
  };

  const handlePharmacyChange = (selectedPharmacyNames) => {
    let selected = [];
    if (Array.isArray(selectedPharmacyNames)) {
      selected = selectedPharmacyNames;
    } else if (selectedPharmacyNames) {
      selected = [selectedPharmacyNames];
    }

    setPharmacyFilter(selected);

    const selectedIds = selected.map(
      (name) => pharmacyNameToIdMap[name] || name,
    );
    setSelectedPharmacyIds(selectedIds);
    setPageIndex(0);
  };

  const handleProgramChange = (selectedProgramNames) => {
    let selected = [];
    if (Array.isArray(selectedProgramNames)) {
      selected = selectedProgramNames;
    } else if (selectedProgramNames) {
      selected = [selectedProgramNames];
    }

    setProgramFilter(selected);

    const selectedIds = selected.map(
      (name) => programNameToIdMap[name] || name,
    );
    setSelectedProgramIds(selectedIds);
    setPageIndex(0);
  };

  const getCurrentSection = () => {
    if (isPriorityOutreach) return "priority-outreach";
    if (isMemberMonitoring) return "monitoring";
    return "members";
  };
  const [activeSection] = useState(getCurrentSection());
  const currentSection = activeSection ? SECTIONS[activeSection] : null;
  const sectionTitle = currentSection?.title || "";
  const sectionDescription = currentSection?.description || "";
  const [containerScale, setContainerScale] = useState(1);

  useEffect(() => {
    const inches = getScreenInches();
    if (inches < 14) {
      setContainerScale(0.9);
    } else {
      setContainerScale(1);
    }
  }, []);

  const quickViewMemberData = React.useMemo(() => {
    if (!selectedMember) return null;
    return {
      name: "loading...",
      memberId: selectedMember.key,
      personCode: `${selectedMember.member?.memberId}-Subscriber/Policy Holder`,
      status: selectedMember.recentActivity?.status || "Active",
      gender: selectedMember.member?.gender || "",
      dateOfBirth: selectedMember.member?.dob || "",
      memberDataId: selectedMember.member?.id || ["None"],
      contact: {
        phone: selectedMember.provider?.contact || "",
        email:
          selectedMember?.member?.email &&
          selectedMember?.member?.email.trim() !== ""
            ? selectedMember.member.email
            : "N/A",
        language: selectedMember.member?.language || "",
        timeZone: selectedMember.member?.timezone || "",
        address: selectedMember.member?.address || "",
      },
      notes: Array.isArray(selectedMember.notes) ? selectedMember.notes : [],
      key: selectedMember.key,
    };
  }, [selectedMember]);

  return (
    <Box
      sx={{
        display: "flex",

        backgroundColor: "transparent",
      }}
    >
      <CssBaseline />
      <MainContent open={mobileOpen} className="search-page">
        <div className="search-page-container">
          <div
            className="search-page-content"
            style={{
              transform: `scale(${containerScale})`,
              transformOrigin: "top left",
              width: containerScale < 1 ? "111%" : "100%",
              minHeight: "92vh",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                marginBottom: "-10px",
                minHeight: "55px",
                fontSize: "18px",
                fontWeight: 600,
                lineHeight: "30px",
                letterSpacing: "0%",
                color: "#080A0E",
                "& a, & span:last-child": {
                  color: "#080A0E",
                  fontSize: "18px",
                  fontWeight: 700,
                  lineHeight: "30px",
                  letterSpacing: "0%",
                  textDecoration: "none",
                },
                "& a:hover": {
                  textDecoration: "underline",
                },
                "& .breadcrumb-separator": {
                  margin: "0 4px",
                  color: "#080A0E",
                },
              }}
            >
              {(() => {
                const getBreadcrumbs = () => {
                  if (isPriorityOutreach) {
                    return [
                      {
                        label: "Pharmacy Outreach",
                        path: "/pharmacy-outreach",
                      },
                      {
                        label: "Priority Outreach",
                        path: "",
                      },
                    ];
                  }
                  if (isMemberMonitoring) {
                    return [
                      { label: "Pharmacy Outreach", path: "/pharmacy" },
                      {
                        label: "Member Monitoring",
                        path: "",
                      },
                    ];
                  }
                  return [{ label: "Pharmacy Outreach", path: "" }];
                };
                const breadcrumbs = getBreadcrumbs();
                return breadcrumbs.map((breadcrumb, index) => (
                  <React.Fragment
                    key={`breadcrumb-${breadcrumb.label || ""}-${breadcrumb.path || ""}-${index}`}
                  >
                    {index > 0 && (
                      <span className="breadcrumb-separator">&gt;</span>
                    )}
                    {breadcrumb.path ? (
                      <Link to={breadcrumb.path}>{breadcrumb.label}</Link>
                    ) : (
                      <span style={{ cursor: "default" }}>
                        {breadcrumb.label}
                      </span>
                    )}
                  </React.Fragment>
                ));
              })()}
            </Box>

            <div className="search-header" style={{ margin: "0 0 16px 0" }}>
              <PageHeader
                title={sectionTitle}
                description={sectionDescription}
              />
            </div>

            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                margin: "0 0 16px 0",
                width: "100%",
              }}
            >
              <div
                style={{ display: "flex", gap: "48px", alignItems: "center" }}
              >
                <div style={{ width: isMobile ? "" : "280px" }}>
                  <CommonSearchBar
                    onSearch={handleSearch}
                    isReset={isResetSearch}
                    ref={searchRef}
                    placeholder="Search members,pharmacies,programs..."
                  />
                </div>
                <Button
                  variant="outlined"
                  startIcon={
                    activeFilterCount > 0 && (
                      <Box
                        sx={{
                          backgroundColor: "#403C6F",
                          color: "white",
                          fontSize: "9px",
                          fontWeight: 400,
                          height: "16px",
                          width: "16px",
                          borderRadius: "50%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          lineHeight: 1,
                          padding: 0,
                          marginRight: "-5px",
                          "& > span": {
                            position: "relative",
                            top: "0.5px",
                            fontSize: "12px",
                            lineHeight: 1,
                          },
                        }}
                      >
                        <span>{activeFilterCount}</span>
                      </Box>
                    )
                  }
                  endIcon={
                    <FilterListRoundedIcon style={{ fontSize: "18px" }} />
                  }
                  onClick={() => setFiltersVisible(!filtersVisible)}
                  sx={{
                    height: "31px",
                    minWidth: "90px",
                    borderRadius: "48px",
                    border: "1px solid #E8E8E9",
                    padding: "6px 12px 6px 16px",
                    textTransform: "none",
                    color: "#403C6F",
                    fontSize: "14px",
                    fontWeight: 500,
                    lineHeight: "20px",
                    letterSpacing: "0.1px",
                    gap: "4px",
                    backgroundColor: (() => {
                      if (activeFilterCount > 0) return "#FAFAFF";
                      if (filtersVisible) return "#ECECF1";
                      return "#FFFFFF";
                    })(),
                    "& .MuiButton-endIcon": {
                      marginLeft: "4px",
                      marginRight: "-4px",
                    },
                    "&:hover": {
                      borderColor: "#403C6F",
                      backgroundColor: filtersVisible
                        ? "#ECECF1"
                        : "rgba(64, 60, 111, 0.04)",
                    },
                  }}
                >
                  Filter
                </Button>
              </div>
              {!isMobile && <StatusIndicators />}
            </div>

            {isMobile && (
              <div style={{ marginBottom: "16px" }}>
                <StatusIndicators />
              </div>
            )}

            {filtersVisible && (
              <div className="search-filters-row">
                <MobileFilterWrapper>
                  {isMobile ? (
                    <MobileCommonFilters
                      members={Array.isArray(members) ? members : []}
                      providers={providers}
                      pharmacies={pharmacies}
                      programs={programs}
                      memberFilter={memberFilter}
                      providerFilter={providerFilter}
                      pharmacyFilter={pharmacyFilter}
                      programFilter={programFilter}
                      onMemberChange={handleMemberChange}
                      onProviderChange={handleProviderChange}
                      onPharmacyChange={handlePharmacyChange}
                      onProgramChange={handleProgramChange}
                      onSearch={handleFilterSearch}
                      onOpenDropdown={() => {}}
                      onLoadMoreOptions={() => {}}
                      loadingMap={{}}
                      hasMoreMap={{}}
                      autoPrefetch={false}
                      compact={isMobile}
                      isPharmacy={true} // Important to show relevant filters
                      showDisposition={false}
                      showAssignee={false}
                    />
                  ) : (
                    <OldCommonFilters
                      members={Array.isArray(members) ? members : []}
                      providers={providers}
                      pharmacies={pharmacies}
                      programs={programs}
                      memberFilter={memberFilter}
                      providerFilter={providerFilter}
                      pharmacyFilter={pharmacyFilter}
                      programFilter={programFilter}
                      onMemberChange={handleMemberChange}
                      onProviderChange={handleProviderChange}
                      onPharmacyChange={handlePharmacyChange}
                      onProgramChange={handleProgramChange}
                      onSearch={handleFilterSearch}
                      isProvider={true}
                    />
                  )}
                </MobileFilterWrapper>
              </div>
            )}

            <div className="search-results-container">
              {error && <p style={{ color: "red" }}>{error}</p>}
              {!error && (
                <div style={{ marginBottom: "16px" }}>
                  <CommonTable
                    ref={tableRef}
                    columns={columns}
                    data={results.content || []}
                    filtersVisible={filtersVisible}
                    loading={loading}
                    pageSize={pageSize}
                    paginate={false}
                    tableHeight={
                      filtersVisible
                        ? "calc(100dvh - 260px)"
                        : "calc(100dvh - 180px)"
                    }
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </MainContent>

      {quickViewMember && selectedMember && (
        <MemberPharmacyQuickview
          onClose={handleCloseQuickView}
          memberData={quickViewMemberData}
          onNoteUpdate={(updatedNotes) => {
            if (selectedMember?.member?.memberId) {
              localStorage.setItem(
                `member_${selectedMember?.member?.memberId}_notes`,
                JSON.stringify(updatedNotes),
              );
            }
          }}
        />
      )}
    </Box>
  );
};

ProviderOutreach.propTypes = {
  isPriorityOutreach: PropTypes.bool,
};

export default ProviderOutreach;
